import { TFunction } from "i18next";

import { DealerUsersProps } from "store/modules/DealerUsers/types.store";
import {
  CoverageAreaDriverRowDto,
  CoverageAreaOrderDetailDto,
} from "types/coverageAreaOrderDetail";

import {
  DriverGroupKey,
  DriverSelectOption,
  NO_DRIVER_OPTION_ID,
} from "./types";
import type { DriverInsightSnapshot } from "types/dealerDriverInsight";

interface BuildDriverSelectOptionsParams {
  coverageAreaDetail: CoverageAreaOrderDetailDto | null;
  dealerUsers: DealerUsersProps[] | undefined;
  driverInsightsById?: Map<string, DriverInsightSnapshot>;
  t: TFunction;
}

export function collectListedDriverIds(
  coverageAreaDetail: CoverageAreaOrderDetailDto | null,
  dealerUsers: DealerUsersProps[] | undefined,
): string[] {
  const driverIds = new Set<string>();

  coverageAreaDetail?.drivers?.forEach((row) => {
    if (row.driverId) driverIds.add(row.driverId);
  });

  dealerUsers?.forEach((user) => {
    if (user.id) driverIds.add(user.id);
  });

  return Array.from(driverIds);
}

const resolveDriverInsightFields = (
  driverId: string,
  fallbackEnabled: boolean | undefined,
  driverInsightsById?: Map<string, DriverInsightSnapshot>,
): Pick<DriverSelectOption, "enabled" | "totalActiveOrders"> => {
  const insight = driverInsightsById?.get(driverId);
  if (!insight) {
    return { enabled: fallbackEnabled };
  }

  const { totalActiveOrders } = insight;
  return {
    enabled: totalActiveOrders === 0,
    totalActiveOrders: totalActiveOrders > 0 ? totalActiveOrders : undefined,
  };
};

const toDriverOption = (
  driver: {
    id: string;
    name: string;
    enabled?: boolean;
  },
  groupKey: DriverGroupKey,
  driverInsightsById?: Map<string, DriverInsightSnapshot>,
): DriverSelectOption => {
  const displayName = (driver.name ?? "").trim();
  const insightFields = resolveDriverInsightFields(
    driver.id,
    driver.enabled,
    driverInsightsById,
  );

  return {
    id: driver.id,
    label: displayName,
    groupKey,
    nameForAssignment: displayName,
    ...insightFields,
  };
};

const sortByPriorityAsc = (
  a: CoverageAreaDriverRowDto,
  b: CoverageAreaDriverRowDto,
): number =>
  (a.priority ?? Number.MAX_SAFE_INTEGER) -
  (b.priority ?? Number.MAX_SAFE_INTEGER);

const buildEligibleDriverIds = (
  dealerUsers: DealerUsersProps[] | undefined,
): Set<string> =>
  new Set(dealerUsers?.map((user) => user.id).filter(Boolean) ?? []);

const findDealerUserById = (
  dealerUsers: DealerUsersProps[] | undefined,
  driverId: string,
): DealerUsersProps | undefined =>
  dealerUsers?.find((user) => user.id === driverId);

const formatDealerUserDisplayName = (user: DealerUsersProps): string =>
  (user.name ?? "").trim();

const buildMapOptions = (
  coverageAreaDetail: CoverageAreaOrderDetailDto | null,
  dealerUsers: DealerUsersProps[] | undefined,
  eligibleDriverIds: Set<string>,
  driverInsightsById?: Map<string, DriverInsightSnapshot>,
): { options: DriverSelectOption[]; idsInMap: Set<string> } => {
  const idsInMap = new Set<string>();
  const options: DriverSelectOption[] = [];

  if (!coverageAreaDetail) {
    return { options, idsInMap };
  }

  const driversRows = (coverageAreaDetail.drivers ?? [])
    .slice()
    .sort(sortByPriorityAsc);

  driversRows.forEach((row) => {
    const { driverId } = row;
    if (
      !driverId ||
      idsInMap.has(driverId) ||
      !eligibleDriverIds.has(driverId)
    ) {
      return;
    }

    const user = findDealerUserById(dealerUsers, driverId);
    const displayName = user ? formatDealerUserDisplayName(user) : "";
    if (!displayName) {
      return;
    }

    idsInMap.add(driverId);
    options.push(
      toDriverOption(
        {
          id: driverId,
          name: displayName,
          enabled: user?.enabled,
        },
        "map",
        driverInsightsById,
      ),
    );
  });

  return { options, idsInMap };
};

const buildOtherOptions = (
  dealerUsers: DealerUsersProps[] | undefined,
  idsInMap: Set<string>,
  driverInsightsById?: Map<string, DriverInsightSnapshot>,
): DriverSelectOption[] => {
  if (!dealerUsers?.length) {
    return [];
  }

  return dealerUsers
    .map((user) => ({
      user,
      displayName: formatDealerUserDisplayName(user),
    }))
    .filter(({ user, displayName }) =>
      Boolean(user?.id && displayName && !idsInMap.has(user.id)),
    )
    .map(({ user, displayName }) =>
      toDriverOption(
        { id: user.id, name: displayName, enabled: user.enabled },
        "other",
        driverInsightsById,
      ),
    );
};

const buildNoDriverOption = (t: TFunction): DriverSelectOption => ({
  id: NO_DRIVER_OPTION_ID,
  label: t("noDeliveryDriver"),
  groupKey: "none",
  nameForAssignment: "",
});

export function buildDriverSelectOptions({
  coverageAreaDetail,
  dealerUsers,
  driverInsightsById,
  t,
}: BuildDriverSelectOptionsParams): DriverSelectOption[] {
  const eligibleDriverIds = buildEligibleDriverIds(dealerUsers);
  const { options: mapOptions, idsInMap } = buildMapOptions(
    coverageAreaDetail,
    dealerUsers,
    eligibleDriverIds,
    driverInsightsById,
  );

  const otherOptions = buildOtherOptions(
    dealerUsers,
    idsInMap,
    driverInsightsById,
  );

  return [buildNoDriverOption(t), ...mapOptions, ...otherOptions];
}
